import { add, addDays, format, parseISO } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';
import { ProviderAvailabilityView, ProviderRateTypeEnum, ShiftTypeEnum } from '../../../../api';
import { DATE_FORMAT } from '../../../constants/time';
import { IShiftPreset, Shift } from '../ShiftPreset';
import { AVAILABILITY_DAY_FORMAT, providerPresets } from './ProviderShiftSelectorModal.const';
import { ProviderAvailability, ShiftSetType } from './ProviderShiftSelectorModal.types';

export const getProviderPresets = ({
  index,
  defaultPresets = providerPresets,
}: {
  index: number;
  defaultPresets?: IShiftPreset[];
}): IShiftPreset[] => {
  return defaultPresets.map((el, idx) => {
    return cloneDeep({ ...el, parentIndex: index, active: idx === 0 });
  });
};

export const getAvailabilitiesRange = (
  dates: Date[],
  shifts: {
    startOn: string;
    presetType: ShiftTypeEnum | ProviderRateTypeEnum;
  }[],
  presets: IShiftPreset[],
  dayFormat = AVAILABILITY_DAY_FORMAT
): ShiftSetType => {
  if (!dates.length) {
    return {
      dates: [],
      shifts: [],
      range: ['', ''],
      presets,
      key: '0',
    };
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const sorted = dates.sort((a, b) => a - b);
  const to = sorted.length > 1 ? sorted[sorted.length - 1] : sorted[0];
  return {
    dates: sorted,
    shifts,
    range: [format(sorted[0], dayFormat), format(to, dayFormat)],
    presets,
    key: uuidv4(),
  };
};

export const getShiftDates = (range: [string, string]) => {
  const [from, to] = range;
  return {
    from,
    to,
  };
};

export const getDisableDates = (schedules: ProviderAvailabilityView[]): Date[] => {
  const dates = schedules.reduce((acc, elem) => {
    acc.push(elem.start_on);
    return acc;
  }, [] as string[]);
  const uniqueDates = new Set(dates);
  return Array.from(uniqueDates).map((date) => new Date(date));
};

export const getInitialShifts = (selectedDates: Date[], initActivePreset: IShiftPreset) => {
  const { type: presetType } = initActivePreset;
  return selectedDates.map((selectedDate) => {
    const startOn = format(selectedDate, DATE_FORMAT);
    return {
      startOn,
      presetType,
    };
  });
};

export const getInitialFormValuesForProviderAvailability = ({
  selectedDates,
  defaultPresets = providerPresets,
  dayFormat = AVAILABILITY_DAY_FORMAT,
  index = 0,
}: {
  selectedDates: Date[];
  defaultPresets?: IShiftPreset[];
  dayFormat?: string;
  index?: number;
}): ShiftSetType => {
  const presets = getProviderPresets({
    defaultPresets,
    index,
  });
  const shifts = getInitialShifts(selectedDates, presets[0]);
  return getAvailabilitiesRange(selectedDates, shifts, presets, dayFormat);
};

export const getUpdatesDateTime = (shift: ProviderAvailability, preset: IShiftPreset) => {
  const { startOn } = shift;
  const dateValue = parseISO(startOn);
  const nextDay = add(dateValue, { days: 1 });

  const endOn =
    preset.type === ShiftTypeEnum.OVERNIGHT || preset.type === ShiftTypeEnum.ANYTIME
      ? format(nextDay, DATE_FORMAT)
      : format(dateValue, DATE_FORMAT);
  return {
    startOn,
    endOn,

    presetType: preset.type,
  };
};

export const getDatesFromRange = (range: { from: string; to: string }): Date[] => {
  const { from, to } = range;
  let current: Date = new Date(from);
  const end = new Date(to);
  const result: Date[] = [];
  while (current <= end) {
    result.push(current);
    current = addDays(current, 1);
  }
  return result;
};

export const validateIfIsDatesInThePast = (range: {
  from: string;
  to: string;
}): { from: string | null; to: string | null } => {
  if (new Date(range.from) < new Date()) {
    return { from: 'Start date cannot be in the past.', to: null };
  }
  if (new Date(range.to) < new Date()) {
    return { from: null, to: 'End date cannot be in the past.' };
  }
  return { from: null, to: null };
};

export const validateDateRange = (
  range: { from: string; to: string },
  bookedDates: Date[],
  datePickerDateFormat = 'MM/dd/yyyy'
): { from: string | null; to: string | null } => {
  if (!bookedDates?.length) {
    return { from: null, to: null };
  }
  const dates = getDatesFromRange(range);
  const bookingSet = new Set(bookedDates.map((el) => format(el, datePickerDateFormat)));
  const checkForBooking = dates.some((date) => {
    return bookingSet.has(format(date, datePickerDateFormat));
  });
  if (checkForBooking) {
    return { from: 'Some dates are already booked', to: 'Some dates are already booked' };
  }
  return { from: null, to: null };
};

export const formatProviderAvailabilityLabel = (schedule: Shift): string => {
  const { availability } = schedule;
  switch (true) {
    case availability === ShiftTypeEnum.OVERNIGHT:
      return 'Overnight';
    case availability === ShiftTypeEnum.DAYTIME:
      return 'Daytime';
    case availability === ShiftTypeEnum.ANYTIME:
      return 'Anytime';
    default:
      return 'Daytime';
  }
};
